/**
 * Botões de toque — retângulo desenhado + rótulo em bitmap font.
 *
 * Existem porque o jogo é jogável no celular mas NÃO é operável: o lobby é
 * dirigido por teclas (C cria, J entra, digitar o código, ENTER começa) e o
 * ESC é a única saída da partida. Num telefone não há nenhuma dessas teclas,
 * então o multiplayer inteiro e a saída do jogo eram inalcançáveis com o dedo.
 *
 * Não usam `Phaser.GameObjects.Text` (proibido no cliente, §CLAUDE.md) nem
 * DOM: o canvas é ampliado por fator inteiro e qualquer camada HTML por cima
 * traria de volta a fonte antialiasada do navegador, justamente o que os
 * atlas 1-bit existem para evitar.
 */

import Phaser from 'phaser'
import { pixelText, type PixelSize } from './text'

/**
 * Alvo mínimo confortável para polegar, em pixels LÓGICOS.
 *
 * A tela tem 256×384 lógicos e é ampliada por fator inteiro (≥2 em celular),
 * então 22 lógicos são ≥44 físicos — o mínimo que as diretrizes de toque da
 * Apple e do Google recomendam, e mais do que o dobro do que o menu oferecia
 * antes: lá o alvo era a caixa exata do glifo, e um toque 9px fora não fazia
 * nada. Medido com emulação de Pixel 7.
 */
export const TOUCH_MIN = 22

/** Este aparelho tem dedo? Decide se a UI de toque aparece. */
export function isTouchDevice(scene: Phaser.Scene): boolean {
  // `pointer: coarse` cobre o caso real (celular/tablet) sem acusar laptop
  // com tela sensível, onde o teclado continua sendo o caminho natural.
  const grosso = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
  return grosso || scene.sys.game.device.input.touch
}

export interface TouchButtonOptions {
  size?: PixelSize
  /** Cor do rótulo. */
  color?: string
  /** Preenchimento; `null` desenha só a borda. */
  fill?: number | null
  border?: number
  depth?: number
}

/**
 * Botão retangular centrado em (x, y). Devolve as partes para quem precisar
 * mexer depois (texto, visibilidade); a cena cuida de destruir.
 */
export class TouchButton {
  readonly zone: Phaser.GameObjects.Zone
  private readonly gfx: Phaser.GameObjects.Graphics
  private readonly label: Phaser.GameObjects.BitmapText
  private pressed = false

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    largura: number,
    altura: number,
    texto: string,
    aoTocar: () => void,
    opts: TouchButtonOptions = {},
  ) {
    const depth = opts.depth ?? 20
    const fill = opts.fill === undefined ? 0x16283a : opts.fill
    const border = opts.border ?? 0x5d7183

    this.gfx = scene.add.graphics().setDepth(depth)
    this.label = pixelText(scene, x, y, opts.size ?? 12, texto, {
      color: opts.color ?? '#f2f2f2',
      align: 'center',
      origin: [0.5, 0.5],
      depth: depth + 1,
    })

    // Zone em vez de tornar o TEXTO interativo: o alvo tem de ser o botão
    // inteiro, não o desenho da letra. Foi exatamente essa diferença que
    // deixou o menu quase intocável.
    this.zone = scene.add
      .zone(x, y, largura, altura)
      .setOrigin(0.5, 0.5)
      .setDepth(depth + 2)
      .setInteractive({ useHandCursor: true })

    const desenhar = (): void => {
      this.gfx.clear()
      if (fill !== null) this.gfx.fillStyle(fill, this.pressed ? 1 : 0.85)
      if (fill !== null) this.gfx.fillRect(x - largura / 2, y - altura / 2, largura, altura)
      this.gfx.lineStyle(1, this.pressed ? 0xffd23c : border, 1)
      this.gfx.strokeRect(x - largura / 2 + 0.5, y - altura / 2 + 0.5, largura - 1, altura - 1)
    }
    desenhar()

    this.zone.on('pointerdown', () => {
      this.pressed = true
      desenhar()
    })
    // Age no `pointerup`, e não no down: o dedo que desliza para fora sem
    // soltar desiste, que é o que qualquer botão de sistema faz.
    this.zone.on('pointerup', () => {
      this.pressed = false
      desenhar()
      aoTocar()
    })
    const solta = (): void => {
      this.pressed = false
      desenhar()
    }
    this.zone.on('pointerout', solta)
    this.zone.on('pointerupoutside', solta)
  }

  setText(texto: string): void {
    this.label.setText(texto)
    this.label.setOrigin(0.5, 0.5)
  }

  setVisible(v: boolean): void {
    this.gfx.setVisible(v)
    this.label.setVisible(v)
    // Zone invisível continua capturando toque — desligar a interação é o que
    // realmente a tira do caminho.
    if (v) this.zone.setInteractive()
    else this.zone.disableInteractive()
  }

  destroy(): void {
    this.gfx.destroy()
    this.label.destroy()
    this.zone.destroy()
  }
}
